import { Feature } from '@pubstack/common/src/rolesAndFeatures'
import { Scope } from '@pubstack/common/src/scope'
import { useAtomValue } from 'jotai'
import { useMemo } from 'react'
import { useUser } from '~/auth/user.hooks'
import { currentScopeState } from '~/state'

export type ScopeWithFeatures = Scope & {
  hasFeature: (feature: Feature) => boolean
}

/**
 * The current scope, with the counterpart to `user.hasFeature`.
 *
 * The two answer different questions and are not interchangeable:
 *
 * - `user.hasFeature(f)` — can the person looking at this use it. A Pubstack user gets every feature
 *   synthesised for whichever scope they are viewing, so staff can operate any account.
 * - `scope.hasFeature(f)` — has this customer been sold it. The same answer whoever is looking.
 *
 * Reach for this one whenever the UI is describing the account rather than gating the viewer — an
 * upgrade pitch, a product badge, anything a Pubstack user should see exactly as the customer does.
 * Use `user.hasFeature` to decide what the viewer is allowed to do.
 */
export const useScope = (): ScopeWithFeatures => {
  const scope = useAtomValue(currentScopeState)
  const user = useUser()

  return useMemo(
    () => ({
      ...scope,
      hasFeature: (feature: Feature) => {
        // `accessFeatures` is absent against a backend that predates it — while this rolls out, and on
        // a frontend pointed at an older environment. Falling back to the viewer's own features
        // degrades to exactly today's behaviour (right for customers, over-reporting for staff), where
        // treating "absent" as "owns nothing" would pitch every product to everyone.
        if (!scope.accessFeatures) {
          return user?.hasFeature(feature) ?? false
        }
        return scope.accessFeatures.includes(feature)
      },
    }),
    [scope, user]
  )
}
